<template>
  <div class="tw:flex tw:flex-1 tw:flex-col tw:overflow-auto">
    <template v-if="showContentTypes">
      <StreamFiltersContentTypes
        v-model:excluded-license="excludedLicense"
        data-testid="content-types"
      />
      <BaseSeparator />
    </template>
    <template v-if="appConfig.platformName === 'CisionOne'">
      <StreamFiltersLanguages
        v-model:excluded-language-codes="excludedLanguageCodes"
        v-model:language-codes="languageCodes"
      />
      <BaseSeparator />
    </template>
    <template v-if="!hideTags">
      <StreamFiltersTags
        v-model:excluded-tags="excludedTags"
        v-model:tags="tags"
        :stream="stream"
      />
      <BaseSeparator />
    </template>
    <StreamFiltersAuthors
      v-model:authors="authors"
      v-model:excluded-authors="excludedAuthors"
    />
    <BaseSeparator />
    <StreamFiltersMinMaxFilter
      v-model="wordCount"
      v-model:enabled="wordCountEnabled"
      class="tw:p-4"
    >
      <template #heading>
        {{ getLocaleText("stream_filters_content.word_count") }}
      </template>
    </StreamFiltersMinMaxFilter>
    <BaseSeparator />
    <StreamFiltersMinMaxFilter
      v-model="pageNumbers"
      v-model:enabled="pageNumbersEnabled"
      class="tw:p-4"
    >
      <template #heading>
        {{ getLocaleText("stream_filters_content.page_number") }}
      </template>
    </StreamFiltersMinMaxFilter>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import BaseSeparator from "shared/components/base/BaseSeparator.vue";
import type {
  StreamFiltersAuthor,
  StreamFiltersLicense,
  StreamFiltersMinMax,
  StreamFiltersTag,
} from "shared/helpers/StreamFilters";
import { type AppConfig } from "shared/services/appConfig/types";
import useGlobalStore from "shared/stores/global";
import useUserStore from "shared/stores/user";
import type { Stream } from "shared/types";

import StreamFiltersAuthors from "./StreamFiltersAuthors.vue";
import StreamFiltersContentTypes from "./StreamFiltersContentTypes.vue";
import StreamFiltersLanguages from "./StreamFiltersLanguages.vue";
import StreamFiltersMinMaxFilter from "./StreamFiltersMinMax.vue";
import StreamFiltersTags from "./StreamFiltersTags.vue";

interface StreamFiltersMediaProps {
  stream: Stream;
  hideTags?: boolean;
}

defineProps<StreamFiltersMediaProps>();

const globalStore = useGlobalStore();

const { appConfig } = storeToRefs(globalStore) as unknown as {
  appConfig: AppConfig;
};

const userStore = useUserStore();

const { organisation } = storeToRefs(userStore);

const showContentTypes = computed<boolean>(() =>
  organisation.value.features.some(
    (feature) => feature.includes("nla") || feature.includes("cla")
  )
);

const excludedLicense = defineModel<StreamFiltersLicense[]>("excludedLicense", {
  default: () => [],
});

const languageCodes = defineModel<string[]>("languageCodes", {
  default: () => [],
});

const excludedLanguageCodes = defineModel<string[]>("excludedLanguageCodes", {
  default: () => [],
});

const tags = defineModel<StreamFiltersTag[]>("tags", {
  default: () => [],
});

const excludedTags = defineModel<StreamFiltersTag[]>("excludedTags", {
  default: () => [],
});

const authors = defineModel<StreamFiltersAuthor[]>("authors", {
  default: () => [],
});

const excludedAuthors = defineModel<StreamFiltersAuthor[]>("excludedAuthors", {
  default: () => [],
});

const wordCount = defineModel<StreamFiltersMinMax>("wordCount", {
  required: true,
});

const wordCountEnabled = defineModel<boolean>("wordCountEnabled");

const pageNumbers = defineModel<StreamFiltersMinMax>("pageNumbers", {
  required: true,
});

const pageNumbersEnabled = defineModel<boolean>("pageNumbersEnabled");
</script>
